import { useState, useEffect } from 'react';
import { useAuth } from 'contexts/AuthContext';
import { customerApiService, CompanyResponse } from 'services/api';

interface CompanyProfileData {
  company: CompanyResponse | null;
  loading: boolean;
  error: string | null;
}

export const useCompanyProfile = () => {
  const { user, company } = useAuth();
  const [companyProfileData, setCompanyProfileData] = useState<CompanyProfileData>({
    company: null,
    loading: true,
    error: null
  });

  const fetchCompanyProfile = async () => {
    if (!user || !company) {
      setCompanyProfileData(prev => ({ ...prev, loading: false }));
      return;
    }

    try {
      setCompanyProfileData(prev => ({ ...prev, loading: true, error: null }));

      const response = await customerApiService.getCompanyProfile();

      if (response.success && response.data) {
        setCompanyProfileData({
          company: response.data,
          loading: false,
          error: null
        });
      } else {
        throw new Error(response.message || 'Failed to fetch company profile');
      }
    } catch (error) {
      console.error('Error fetching company profile:', error);
      setCompanyProfileData(prev => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to fetch company profile'
      }));
    }
  };

  const updateCompanyProfile = async (updates: Partial<CompanyResponse>) => {
    try {
      const response = await customerApiService.updateCompanyProfile(updates);

      if (response.success) {
        await fetchCompanyProfile(); // Refresh the data
        return { success: true, data: response.data };
      } else {
        throw new Error(response.message || 'Failed to update company profile');
      }
    } catch (error) {
      console.error('Error updating company profile:', error);
      throw error;
    }
  };

  const refreshData = () => {
    fetchCompanyProfile();
  };

  useEffect(() => {
    fetchCompanyProfile();
  }, [user, company]);

  return {
    ...companyProfileData,
    updateCompanyProfile,
    refreshData
  };
};
